import type { CityConfig } from "../hawaii-city/types";

export const kaneoheConfig: CityConfig = {
  city: "Kaneohe",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "kaneohe",
  route: "/kaneohe",
  title: "Emergency & Expedited Passport Services in Kaneohe, HI — Fast Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Kaneohe, HI. Fast passport renewal and urgent passport help for Windward Oahu and military families. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Kaneohe, HI",
  heroSubtitle: "Kaneohe, Kahaluu, and Windward Oahu residents — get your passport fast without driving through Honolulu traffic. Start online, ship overnight, and we handle the rest.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20Kaneohe%20Bay%20Hawaii%20with%20lush%20green%20Ko%CA%BBolau%20Mountains%20in%20background%2C%20calm%20turquoise%20waters%2C%20tropical%20palm%20trees%20along%20shoreline%2C%20warm%20golden%20afternoon%20light%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=kaneohe-hero-01&orientation=landscape",
  heroImageAlt: "Kaneohe Bay Hawaii with lush mountains and turquoise water",
  geoPosition: "21.4185;-157.8035",
  geoPlacename: "Kaneohe, Hawaii",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 15 miles southwest in downtown Honolulu",
  },
  nearbyCitiesZips: [
    { name: "Kaneohe", zips: "96744" },
    { name: "Kailua", zips: "96734" },
    { name: "Kahaluu", zips: "96744" },
    { name: "Ahuimanu", zips: "96744" },
    { name: "Laie", zips: "96762" },
    { name: "Kahuku", zips: "96731" },
    { name: "Hauula", zips: "96717" },
    { name: "Kaaawa", zips: "96730" },
    { name: "Punaluu", zips: "96717" },
    { name: "Honolulu", zips: "96815" },
    { name: "Waikiki", zips: "96815" },
    { name: "Pearl City", zips: "96782" },
    { name: "Haleiwa", zips: "96712" },
    { name: "Waialua", zips: "96791" },
    { name: "Pupukea", zips: "96712" },
    { name: "Waimanalo", zips: "96795" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Kaneohe home in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Kaneohe or Kailua to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Windward Oahu residents." },
  ],
  testimonials: [
    {
      name: "Nicole R.",
      location: "Kaneohe, HI",
      rating: 5,
      text: "I live in Kaneohe and didn't want to deal with Honolulu traffic for a passport appointment. Fast Passport Center made the whole process so easy — shipped from Kailua FedEx and had my passport in under a week.",
      service: "Passport Renewal",
    },
    {
      name: "Michael H.",
      location: "Kahaluu, HI",
      rating: 5,
      text: "Our family is stationed at MCBH and we needed passports for a PCS move to Japan. FPC handled all six of us with professionalism and speed. The military-friendly service was a huge relief.",
      service: "Family Passport Processing",
    },
    {
      name: "Sarah L.",
      location: "Kailua, HI",
      rating: 5,
      text: "Last-minute trip to Australia and my passport was expired. I'm in Kailua near Kaneohe and FPC had me sorted in 5 days. Their team was incredibly responsive throughout.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "kaneohe-usps",
      name: "Kaneohe Post Office",
      type: "USPS Passport Acceptance",
      address: "45-690 Kamehameha Hwy",
      city: "Kaneohe",
      state: "HI",
      zip: "96744",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Kaneohe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kailua-usps",
      name: "Kailua Post Office",
      type: "USPS Passport Acceptance",
      address: "130 Kailua Rd",
      city: "Kailua",
      state: "HI",
      zip: "96734",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Kailua area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "honolulu-usps-kaneohe",
      name: "Honolulu Airport Post Office",
      type: "USPS Passport Acceptance",
      address: "300 Rodgers Blvd",
      city: "Honolulu",
      state: "HI",
      zip: "96819",
      phone: "800-275-8777",
      hours: "Mon–Fri 8am–4pm",
      distance: "About 15 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Honolulu Airport area. Useful for inter-island and international travelers from Windward Oahu.",
      icon: "ri-mail-line",
    },
    {
      id: "laie-usps",
      name: "Laie Post Office",
      type: "USPS Passport Acceptance",
      address: "55-510 Kamehameha Hwy",
      city: "Laie",
      state: "HI",
      zip: "96762",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 12 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Shore option for Windward Oahu residents. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Kaneohe, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Kaneohe or Kailua post office can take 2–6 months. For Windward Oahu residents, our courier service is the fastest option without driving to Honolulu.",
    },
    {
      q: "Do I need to drive to Honolulu for a passport?",
      a: "No. Most Kaneohe and Windward Oahu travelers start online and ship documents via overnight courier. The Kaneohe and Kailua Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Kaneohe?",
      a: "The Kaneohe Post Office at 45-690 Kamehameha Hwy and the Kailua Post Office at 130 Kailua Rd both accept passport applications by appointment. Laie Post Office is also an option for North Shore residents.",
    },
    {
      q: "Can military families at MCBH Kaneohe Bay use this service?",
      a: "Absolutely. We frequently serve military families at MCBH Kaneohe Bay and across Windward Oahu. Military travel, PCS moves, and TDY often have tight deadlines, and our expedited processing is designed for urgent situations.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Kaneohe or Kailua Post Office are the most convenient options for Windward Oahu residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Kaneohe work?",
      a: "You can ship overnight using FedEx or UPS from Kaneohe or Kailua. Most Windward Oahu residents use the FedEx location near Kailua Town or the UPS Store in Kaneohe.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Kaneohe?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Kaneohe and Kailua families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Kaneohe residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kailua", address: "200 Hamakua Dr, Kailua, HI 96734", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kaneohe", address: "46-047 Kamehameha Hwy, Kaneohe, HI 96744", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Kailua", address: "340 Kuulei Rd, Kailua, HI 96734", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Kailua", address: "600 Kailua Rd, Kailua, HI 96734", hours: "Call for hours" },
    { name: "Walmart — Kailua", address: "Kailua Town Center, Kailua, HI 96734", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Kaneohe DMV — Kaneohe Satellite City Hall",
      address: "45-270 Waikalua Rd, Kaneohe, HI 96744",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kailua DMV — Kailua Satellite City Hall",
      address: "1090 Keolu Dr, Kailua, HI 96734",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Kailua area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "About 15 miles southwest — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Most Windward Oahu travelers fly from HNL for international trips.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Inter-island connection option. Some Windward Oahu residents connect through Maui for international travel.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Kaneohe and Windward Oahu.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Kaneohe travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Kaneohe residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Kaneohe and Kailua families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Kaneohe residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Kaneohe travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Kaneohe residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Kaneohe residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Kaneohe residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Kaneohe, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Kaneohe residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Kaneohe travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Kaneohe travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Kaneohe or Kailua." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to drive to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — downtown Honolulu location", fpc: "Most travelers ship documents overnight from Kaneohe", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Kailua", route: "/kailua" },
    { name: "Honolulu", route: "/honolulu" },
    { name: "Pearl City", route: "/pearl-city" },
    { name: "Hilo", route: "/hilo" },
    { name: "Kapolei", route: "/kapolei" },
    { name: "Mililani", route: "/mililani" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Kaneohe home, ship with our prepaid label from any UPS/FedEx location in Kailua or Kaneohe, or schedule a drop-off. Most Windward Oahu clients ship overnight.",
  fpcOfficeDistance: "Serving Kaneohe and all of Windward Oahu remotely",
  serviceAreaIntro: "Whether you are in Kaneohe, Kailua, Kahaluu, or anywhere on Windward Oahu, Fast Passport Center makes expedited passport processing simple — no driving through Honolulu traffic, no agency lines.",
  nearbyCitiesIntro: "Anywhere on Windward Oahu — start online, ship overnight from Kaneohe or Kailua, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Kaneohe as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Kaneohe travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Kaneohe, Kailua, and Kahaluu who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Kaneohe and Windward Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Kaneohe Bay",
      description: "One of the most beautiful bays in Hawaii, home to the famous 'Sandbar' accessible only by boat. Kaneohe Bay is a local treasure and a symbol of the windward side's raw beauty.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kaneohe%20Bay%20Oahu%20Hawaii%20with%20turquoise%20water%20and%20sandbars%2C%20lush%20green%20Koolau%20Mountains%20in%20background%2C%20boats%20anchored%20in%20calm%20bay%2C%20tropical%20sunny%20day%2C%20professional%20aerial%20travel%20photography%2C%20vibrant%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kaneohe-bay&orientation=landscape",
    },
    {
      name: "Ko'olaupoko Mountains",
      description: "The dramatic Ko'olau mountain range towers behind Kaneohe, creating one of Hawaii's most photogenic landscapes. The mist-shrouded peaks are a backdrop for daily life in this windward community.",
      imageUrl: "https://readdy.ai/api/search-image?query=Koolau%20Mountains%20Windward%20Oahu%20Hawaii%2C%20dramatic%20green%20volcanic%20peaks%20with%20mist%2C%20lush%20tropical%20valley%20below%2C%20waterfalls%20visible%20on%20cliffs%2C%20dramatic%20clouds%2C%20professional%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kaneohe-koolau&orientation=landscape",
    },
    {
      name: "MCBH Kaneohe Bay",
      description: "Marine Corps Base Hawaii occupies the northeastern peninsula of Kaneohe Bay. The military presence defines much of Kaneohe's community character and creates significant international travel demand.",
      imageUrl: "https://readdy.ai/api/search-image?query=Marine%20Corps%20Base%20Hawaii%20aerial%20view%20with%20Kaneohe%20Bay%2C%20military%20base%20on%20tropical%20peninsula%2C%20blue%20ocean%20surrounding%2C%20green%20mountains%20in%20distance%2C%20sunny%20day%2C%20professional%20aerial%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kaneohe-mcbh&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-shield-star-line", title: "MCBH Military Families", description: "Marine Corps Base Hawaii (MCBH) Kaneohe Bay means constant PCS moves, TDY trips, and SOFA travel. We prioritize military urgent passport processing — urgent same-week available." },
    { icon: "ri-flight-takeoff-line", title: "HNL via H-3 or Pali", description: "Kaneohe reaches HNL through the H-3 Tunnel or Pali Highway. Both routes take 20–30 minutes. Have your passport validated before you head to the airport." },
    { icon: "ri-truck-line", title: "Ship from Kaneohe or Kailua", description: "FedEx and UPS locations in Kailua Town handle overnight shipping for Windward Oahu residents. Documents sent before 6pm arrive at our processing center next morning." },
    { icon: "ri-global-line", title: "Japan & Asia Travel Patterns", description: "Kaneohe has a significant Japanese-American community. Obon season, Golden Week, and year-end travel to Japan and Asia create peak passport demand each year." },
    { icon: "ri-leaf-line", title: "Ecotourism & Adventures", description: "Kaneohe Bay kayaking, snorkeling at the Sandbar, and Ko'olau hiking attract international adventure travelers. Keep passport validity checked before planning overseas trips." },
    { icon: "ri-building-line", title: "Windward Access Points", description: "The Kaneohe Satellite City Hall handles state IDs and REAL ID — both useful documents when applying for a passport for the first time." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/", desc: "Hawaii Department of Health — Office of Health Status Monitoring for birth, death, marriage, and divorce certificates statewide" },
    { label: "Marriage Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/marriage-certificates/", desc: "Certified copies of Hawaii marriage certificates from the Department of Health Vital Records Office" },
    { label: "Divorce Decrees (HI Family Court)", url: "https://www.courts.state.hi.us/self-help/court-forms/court-forms-family-court", desc: "Hawaii State Judiciary Family Court — divorce decree records by circuit" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Hawaii birth, death, marriage, and divorce certificates online via VitalChek — fastest state-approved method" },
  ],
};